import { omit, pick } from '@/shared/utils/object';
import {
  BusinessAnalysisInfoVo,
  BusinessSummaryReq,
  BusinessSummaryVo,
  BusinessTypeVo,
  DemandStockRegisterDetailVo,
  DemandStockRegisterInfoQueryReq,
  DemandSummaryReq,
  DemandSummaryVo,
  FinancialSummaryReq,
  FinancialSummaryVo,
  InquiryInfoReq,
  InquiryInfoVo,
  ProjectAddReq,
  ProjectAttachmentUpdateReq,
  ProjectBatchReq,
  ProjectDetailVo,
  ProjectEvalDetailVo,
  ProjectInfoVo,
  ProjectListVo,
  ProjectPactReq,
  ProjectQueryReq,
  ProjectSelectReq,
  ProjectSummaryVo,
  ProjectUpdateReq,
  PurchaseDetailDemandReq,
  PurchaseDetailSupplierReq,
  PurchaseSummaryByCanteenReq,
  PurchaseSummaryByCanteenVo,
  PurchaseSummaryByDemandVo,
  PurchaseSummaryByProductVo,
  PurchaseSummaryBySupplierVo,
  PurchaseSummaryDetailByDemand,
  PurchaseSummaryDetailBySupplier,
  PurchaseSummaryProductDetailReq,
  PurchaseSummaryProductDetailVo,
  QuotationImportSegmentReq,
  RevenueStatisticsReq,
  RevenueStatisticsResultVo,
  SegmentEvalReq,
} from 'typings/openapi';
import { request } from '../http';
import { ReportDateTypeEnum } from './constants';
import {
  GetManagePurchaseReportPurchaseListRequest,
  GetManagePurchaseReportSkuListRequest,
  GetManagePurchaseReportSupplyListRequest,
  GetPurchaserReportSkuListRequest,
  GetPurchaserReportSupplyListRequest,
  GetSupplyPurchaseReportDemandListRequest,
  GetSupplyPurchaseReportSkuListRequest,
} from './types';

/**
 * 供方采购汇总商品
 * @param data
 * @returns
 */
export const GetSupplyPurchaseReportSkuList = (data: GetSupplyPurchaseReportSkuListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByProductVo>>('/purchaseSummary/supplier/byProduct', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};
/**
 * 供方采购汇总需方
 * @param data
 * @returns
 */
export const GetSupplyPurchaseReportDemandList = (data: GetSupplyPurchaseReportDemandListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByDemandVo>>('/purchaseSummary/supplier/byDemand', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 供方采购汇总表 --- 按食堂
 */
export const GetSupplyPurchaseReportDiningList = (
  data: PurchaseSummaryByCanteenReq & { pageSize: number; pageNum: number },
) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByCanteenVo>>('/purchaseSummary/supplier/byCanteen', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 需方采购汇总商品
 * @param data
 * @returns
 */
export const GetPurchaserPurchaseReportSkuList = (data: GetPurchaserReportSkuListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByProductVo>>('/purchaseSummary/demand/byProduct', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 需方采购汇总供方
 * @param data
 * @returns
 */
export const GetPurchaserPurchaseReportSupplyList = (data: GetPurchaserReportSupplyListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryBySupplierVo>>('/purchaseSummary/demand/bySupplier', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 需方采购汇总表 --- 按食堂 */
export const GetPurchaserPurchaseReportDiningList = (
  data: PurchaseSummaryByCanteenReq & { pageSize: number; pageNum: number },
) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByCanteenVo>>('/purchaseSummary/demand/byCanteen', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 采购明细 */
export const GetPurchaserPurchaseReportDetailList = (data: PurchaseSummaryProductDetailReq) => {
  return request<PagingResponse<PurchaseSummaryProductDetailVo>>('/purchaseSummary/product/detail/purchase/summary', {
    method: 'POST',
    data,
  });
};

/**
 * 监管平台采购汇总商品
 * @param data
 * @returns
 */
export const GetManagePurchaseReportSkuList = (data: GetManagePurchaseReportSkuListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByProductVo>>('/purchaseSummary/generating/byProduct', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 监管平台采购汇总需方
 * @param data
 * @returns
 */
export const GetManagePurchaseReportPurchaseList = (data: GetManagePurchaseReportPurchaseListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByDemandVo>>('/purchaseSummary/generating/byDemand', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 监管平台采购汇总供方
 * @param data
 * @returns
 */
export const GetManagePurchaseReportSupplyList = (data: GetManagePurchaseReportSupplyListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryBySupplierVo>>('/purchaseSummary/generating/bySupplier', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 监管平台采购汇总表 --- 按食堂 */
export const GetManagePurchaseReportDiningList = (
  data: PurchaseSummaryByCanteenReq & { pageSize: number; pageNum: number },
) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByCanteenVo>>('/purchaseSummary/generating/byCanteen', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 供方-经营分析 */
export const GetSupplyBusinessAnalysisList = (date: ReportDateTypeEnum) => {
  return request<BusinessAnalysisInfoVo>(`/purchaseSummary/business/analysis/${date}`, {
    method: 'POST',
  });
};

/** 财务汇总表-按日期 */
export const GetFinancialSummaryByDayList = (data: RevenueStatisticsReq) => {
  return request<RevenueStatisticsResultVo>('/revenue/statisticsResultByDay', {
    method: 'POST',
    data,
  });
};

/** 财务汇总表-按月份 */
export const GetFinancialSummaryByMonthList = (data: RevenueStatisticsReq) => {
  return request<RevenueStatisticsResultVo>('/revenue/statisticsResultByMonth', {
    method: 'POST',
    data,
  });
};

/** 监管方财务汇总表 */
export const GetManageFinancialSummaryList = (data: FinancialSummaryReq) => {
  return request<FinancialSummaryVo[]>('/financialSummary/supervisor/queryList', {
    method: 'POST',
    data,
  });
};

/** 询价公示列表 */
export const GetInquiryPublicList = (data: InquiryInfoReq) =>
  request<PagingResponse<InquiryInfoVo>>('/inquiryInfo/inquiryPublicity/list', { method: 'POST', data });

/** 项目公示列表 */
export const GetProjectPublicList = (data: ProjectQueryReq) =>
  request<PagingResponse<ProjectSummaryVo[]>>('/project/summary', { method: 'POST', data });

/** 项目列表 */
export const GetProjectList = (data: ProjectQueryReq) =>
  request<PagingResponse<ProjectListVo[]>>('/project/queryList', { method: 'POST', data });

/** 新建项目 */
export const AddProject = (data: ProjectAddReq) => request('/project/add', { method: 'POST', data });

/** 编辑项目 */
export const EditProject = (data: ProjectUpdateReq) => request('/project/update', { method: 'POST', data });

/** 项目详情 */
export const GetProjectDetail = (projectId: string) =>
  request<ProjectDetailVo>(`/project/detail/${projectId}`, { method: 'GET' });

/** 项目详情-通过单号获取 */
export const GetProjectDetailByNo = (projectNo: string) =>
  request<ProjectDetailVo>(`/project/getProjectDetailByNo/${projectNo}`, { method: 'GET' });

/** 修改项目附件 */
export const UpdateProjectAttachment = (data: ProjectAttachmentUpdateReq) =>
  request('/project/update/attachmentList', { method: 'POST', data });

/** 项目公示详情-带供方信息 */
export const GetProjectDetailWithSupply = (projectId: string) =>
  request<ProjectEvalDetailVo>(`/project/eval/${projectId}`, { method: 'GET' });

/** 批量更新项目状态 (发布、公示、取消公示、关闭、删除) */
export const BatchUpdateProjectStatus = (data: ProjectBatchReq) =>
  request('/project/batchUpdateStatus', { method: 'POST', data });

/** 评标 */
export const UpdateSegmentEval = (data: SegmentEvalReq) =>
  request('/project/updateSegmentEval', { method: 'POST', data });

/** 修改关联报价单 失效/恢复 操作 */
export const UpdateOrderNoStatus = (data: SegmentEvalReq) =>
  request('/project/updateSegmentEval/flag', { method: 'POST', data });

/** 定标 --批量生成采购合同 */
export const BatchCreatePact = (data: ProjectPactReq) => request('/project/create/pact', { method: 'POST', data });

/** 下拉选择项目以及标段 */
export const GetProjectListWithSegment = (data: ProjectSelectReq) =>
  request<PagingResponse<ProjectInfoVo[]>>('/project/select', { method: 'POST', data });

/** 导入标段商品 */
export const ImportSegmentProduct = (data: QuotationImportSegmentReq) =>
  request('/quotation/importSegmentProduct', { method: 'POST', data });

/** 食安统计表 */
export const GetFoodSafetyReportList = (data: BusinessSummaryReq) =>
  request<PagingResponse<BusinessSummaryVo[]>>('/purchaseSummary/request/business/summary', { method: 'POST', data });

/** 食安统计业务列表 */
export const GetFoodSafeBusinessTypeList = () =>
  request<BusinessTypeVo[]>('/purchaseSummary/request/business/type/list', { method: 'GET' });

/** 需方---采购明细表 */
export const GetPurchaseDetailList = (data: PurchaseDetailDemandReq) =>
  request<PagingResponse<PurchaseSummaryDetailByDemand>>('/purchase/detail/demand/list', { method: 'POST', data });

/** 供方---采购明细表 */
export const GetSupplyPurchaseDetailList = (data: PurchaseDetailSupplierReq) =>
  request<PagingResponse<PurchaseSummaryDetailBySupplier>>('/purchase/detail/supplier/list', { method: 'POST', data });

/** 需方查询库存明细 */
export const GetPurchaseStockDetailList = (data: DemandStockRegisterInfoQueryReq) =>
  request<PagingResponse<DemandStockRegisterDetailVo>>('/purchase/detail/demand/register', { method: 'POST', data });

/** 查询需方组汇总 */
export const GetPurchaseGroupSummaryList = (data: DemandSummaryReq) =>
  request<PagingResponse<DemandSummaryVo>>('/demandSummary/supervisor/queryList', { method: 'POST', data });
